import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Users, Search, ChevronLeft, ChevronRight, LogIn, UserCog, Loader2, UserPlus, X, Copy, Check, Dice5, Pause, Play, Trash2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function AdminUsers() {
  const { getAuthHeader, startImpersonation, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const handleImpersonate = async (u) => {
    if (!u?.user_id) return;
    if (!window.confirm(`Log in as ${u.email}?\n\nA banner will be shown at all times while you're using their account. Click "Exit Impersonation" to return to your admin account.`)) return;
    setBusyId(u.user_id);
    const res = await startImpersonation(u.user_id);
    setBusyId(null);
    if (res.success) {
      toast.success(`Now viewing as ${u.email}`);
      window.location.href = "/dashboard";
    } else {
      toast.error(res.error || "Couldn't log in as this user.");
    }
  };

  const handleSetRole = async (u, newRole) => {
    if (!u?.user_id) return;
    setBusyId(u.user_id);
    try {
      await axios.patch(
        `${API}/auth/admin/users/${u.user_id}/role`,
        { role: newRole },
        { headers: getAuthHeader() },
      );
      toast.success(`${u.email} → ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Role update failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (u) => {
    if (!u?.user_id) return;
    const willPause = u.is_active !== false;
    if (willPause && !window.confirm(`Pause ${u.email}?\n\nThey won't be able to log in until you reactivate them. Their listing data stays intact.`)) return;
    setBusyId(u.user_id);
    try {
      await axios.patch(
        `${API}/auth/admin/users/${u.user_id}/active`,
        { is_active: !willPause },
        { headers: getAuthHeader() },
      );
      toast.success(`${u.email} ${willPause ? "paused" : "reactivated"}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update status");
    } finally {
      setBusyId(null);
    }
  };

  const handleChangeEmail = async (u) => {
    if (!u?.user_id) return;
    const newEmail = window.prompt(`Change email for ${u.email}?\n\nEnter the new email address:`, u.email || "");
    if (!newEmail) return;
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || trimmed === (u.email || "").toLowerCase()) {
      toast.error("Enter a different, valid email.");
      return;
    }
    setBusyId(u.user_id);
    try {
      await axios.patch(
        `${API}/auth/admin/users/${u.user_id}/email`,
        { new_email: trimmed },
        { headers: getAuthHeader() },
      );
      toast.success(`Email updated → ${trimmed}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change email.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (u) => {
    if (!u?.user_id) return;
    const confirmed = window.prompt(
      `Permanently delete ${u.email}?\n\nThis cannot be undone. Their listings will be unlinked (orphaned) so they can be re-claimed.\n\nType DELETE to confirm.`,
      "",
    );
    if (confirmed !== "DELETE") {
      if (confirmed !== null) toast.error("Cancelled — must type DELETE exactly.");
      return;
    }
    setBusyId(u.user_id);
    try {
      await axios.delete(`${API}/auth/admin/users/${u.user_id}`, { headers: getAuthHeader() });
      toast.success(`Deleted ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => { fetchUsers(); }, [page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: getAuthHeader() });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Users</h1>
          <p className="text-[#94A3B8] mt-1">{total} registered users</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:opacity-90 text-white self-start"
          data-testid="admin-add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-1.5" />
          Add User
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]" data-testid="admin-users-search" />
        </div>
        <Button type="submit" className="bg-[#7C3AED] hover:bg-[#8B5CF6]">Search</Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : users.length > 0 ? (
        <>
          <div className="bg-[#0F172A] border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-xs text-[#64748B] uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs text-[#64748B] uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="text-left px-4 py-3 text-xs text-[#64748B] uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs text-[#64748B] uppercase tracking-wider hidden sm:table-cell">Joined</th>
                  <th className="text-right px-4 py-3 text-xs text-[#64748B] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {users.map((u, i) => {
                  const isSelf = me?.user_id === u.user_id;
                  const isAdminTarget = u.role === "admin" || u.role === "super_admin";
                  const canImpersonate = !isSelf && !isAdminTarget;
                  const isMeAdmin = me?.role === "admin" || me?.role === "super_admin";
                  const busy = busyId === u.user_id;
                  return (
                  <motion.tr key={u.user_id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                    className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-medium">{(u.first_name || u.email)?.[0]?.toUpperCase()}</span>
                        </div>
                        <span className="text-white text-sm font-medium">{u.first_name} {u.last_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#94A3B8] hidden md:table-cell">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.role === "admin" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                        u.role === "super_admin" ? "bg-[#EF4444]/10 text-[#EF4444]" :
                        u.role === "staff" ? "bg-[#14B8A6]/10 text-[#14B8A6]" :
                        "bg-[#7C3AED]/10 text-[#7C3AED]"
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#64748B] hidden sm:table-cell">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {canImpersonate && (
                          <button
                            onClick={() => handleImpersonate(u)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#7C3AED]/15 hover:bg-[#7C3AED]/30 text-[#A78BFA] text-xs font-medium disabled:opacity-50"
                            title="Log in as this user"
                            data-testid={`impersonate-${u.user_id}`}
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogIn className="w-3 h-3" />}
                            Log in as
                          </button>
                        )}
                        {isMeAdmin && !isAdminTarget && !isSelf && (
                          <select
                            value={u.role}
                            onChange={(e) => handleSetRole(u, e.target.value)}
                            disabled={busy}
                            className="bg-[#0F172A] border border-white/10 text-white text-xs rounded-md px-2 py-1 disabled:opacity-50"
                            style={{ colorScheme: "dark" }}
                            data-testid={`role-select-${u.user_id}`}
                          >
                            <option value="member" className="bg-[#0F172A]">member</option>
                            <option value="staff" className="bg-[#0F172A]">staff</option>
                          </select>
                        )}
                        {isMeAdmin && (
                          <button
                            onClick={() => handleChangeEmail(u)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#94A3B8] text-xs font-medium disabled:opacity-50"
                            title="Change this user's email"
                            data-testid={`change-email-${u.user_id}`}
                          >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="border-white/20 text-white hover:bg-white/5"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-[#94A3B8]">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="border-white/20 text-white hover:bg-white/5"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center">
          <Users className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Users Found</h2>
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <CreateUserModal
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              fetchUsers();
            }}
            getAuthHeader={getAuthHeader}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated, getAuthHeader }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null); // {email, password} once successful
  const [copied, setCopied] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const generatePassword = () => {
    // Readable 14-char password, mixed case + digits + 1 symbol
    const lower = "abcdefghjkmnpqrstuvwxyz";
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const digits = "23456789";
    const sym = "!@#$%&*";
    const pool = lower + upper + digits;
    let pw = "";
    for (let i = 0; i < 13; i++) pw += pool[Math.floor(Math.random() * pool.length)];
    pw += sym[Math.floor(Math.random() * sym.length)];
    update("password", pw);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.error("Fill in all fields.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/admin/users`, form, { headers: getAuthHeader() });
      toast.success(`${form.role} account created for ${form.email}`);
      setCreated({ email: form.email, password: form.password });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create user.");
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = async () => {
    const text = `Login: ${created.email}\nPassword: ${created.password}\nDashboard: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-user-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        className="bg-[#0F172A] border border-white/10 rounded-2xl w-full max-w-md shadow-[0_20px_80px_rgba(124,58,237,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#A78BFA]" />
            {created ? "Account Created" : "Add New User"}
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white p-1" data-testid="create-user-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {created ? (
          <div className="p-5 space-y-4">
            <p className="text-sm text-[#E2E8F0]">
              Account is live. Share these credentials with the user — they can change the password from Settings after first login.
            </p>
            <div className="bg-[#020817] border border-white/10 rounded-lg p-4 font-mono text-sm space-y-1.5">
              <div><span className="text-[#64748B]">Email:</span> <span className="text-white">{created.email}</span></div>
              <div><span className="text-[#64748B]">Password:</span> <span className="text-[#FBBF24]">{created.password}</span></div>
              <div><span className="text-[#64748B]">Login URL:</span> <span className="text-[#7C3AED]">{window.location.origin}/login</span></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={copyCreds} className="flex-1 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white" data-testid="copy-creds-btn">
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                {copied ? "Copied" : "Copy Credentials"}
              </Button>
              <Button onClick={onCreated} variant="outline" className="border-white/20 text-white hover:bg-white/5">Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">First Name</label>
                <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="bg-[#020817] border-white/10 text-white" data-testid="new-user-first-name" />
              </div>
              <div>
                <label className="block text-xs text-[#94A3B8] mb-1.5">Last Name</label>
                <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="bg-[#020817] border-white/10 text-white" data-testid="new-user-last-name" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5">Email</label>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-[#020817] border-white/10 text-white" data-testid="new-user-email" />
            </div>
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5">Initial Password</label>
              <div className="flex gap-2">
                <Input value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="min 8 chars" className="flex-1 bg-[#020817] border-white/10 text-white font-mono" data-testid="new-user-password" />
                <Button type="button" onClick={generatePassword} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="generate-password-btn">
                  <Dice5 className="w-4 h-4 mr-1.5" /> Generate
                </Button>
              </div>
              <p className="text-xs text-[#64748B] mt-1">Share with the user — they can change it from Settings later.</p>
            </div>
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5">Role</label>
              <select value={form.role} onChange={(e) => update("role", e.target.value)} className="w-full bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm" style={{ colorScheme: "dark" }} data-testid="new-user-role">
                <option value="staff" className="bg-[#0F172A]">Staff — can manage any user's listing</option>
                <option value="member" className="bg-[#0F172A]">Member — standard user</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/5">Cancel</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:opacity-90 text-white" data-testid="create-user-submit">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
                {saving ? "Creating…" : "Create User"}
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}
